import {BaseComponent} from '../base/component.base.js';
import {escapeHtml} from '../../core/escape-html.js';
import template from "./user-list.template.html";
import styles from "./user-list.styles.css";

export class UserListComponent extends BaseComponent {
    #resetPasswordUserId = null;

    connectedCallback() {
        this.loadTemplate(template, styles);

        this.$('#filter-form').addEventListener('submit', (e) => {
            e.preventDefault();
            const clientSlug = this.$('#filter-slug').value.trim();
            this.emit('user-filter', {clientSlug});
        });

        this.$('#clear-filter').addEventListener('click', () => {
            this.$('#filter-slug').value = '';
            this.emit('user-filter', {clientSlug: ''});
        });

        this.shadowRoot.addEventListener('click', (e) => this.#handleClick(e));
        this.$('#reset-password-form')?.addEventListener('submit', (e) => this.#handleResetPasswordSubmit(e));
        this.$('#cancel-reset-password')?.addEventListener('click', () => this.closeResetPasswordDialog());
        this.$('#confirm-password')?.addEventListener('input', () => this.#clearResetPasswordValidation());
    }

    async #handleClick(e) {
        const btnRemove = e.target.closest('.btn-remove');
        if (btnRemove) {
            const dialog = document.querySelector('admin-confirm-dialog');
            const confirmed = await dialog.ask({
                title: `Delete user "${btnRemove.dataset.email}"?`,
                body: "They lose access immediately. They can register again later with their client's registration code.",
                confirmLabel: 'Delete user',
                cancelLabel: 'Cancel',
                danger: true,
            });
            if (confirmed) {
                this.emit('user-delete', {id: Number(btnRemove.dataset.id)});
            }
        }

        const btnReset = e.target.closest('.btn-reset-pw');
        if (btnReset) {
            this.#openResetPasswordDialog(Number(btnReset.dataset.id), btnReset.dataset.email);
        }
    }

    #openResetPasswordDialog(id, email) {
        this.#resetPasswordUserId = id;
        const dialog = this.$('#reset-password-dialog');
        const userEl = this.$('#reset-password-user');
        const form = this.$('#reset-password-form');
        if (!dialog || !userEl || !form) return;

        userEl.textContent = `User: ${email}`;
        form.reset();
        this.#clearResetPasswordValidation();
        this.showResetPasswordError(null);
        dialog.showModal();
        this.$('#new-password')?.focus();
    }

    closeResetPasswordDialog() {
        const dialog = this.$('#reset-password-dialog');
        dialog?.close();
        this.#resetPasswordUserId = null;
    }

    /**
     * Shows why the backend rejected the new password inside the reset password dialog, since the
     * modal dialog covers the page and anything shown behind it.
     *
     * @param {string | null} message null hides the error
     * @returns {boolean} whether the dialog is open, so the message is on screen
     */
    showResetPasswordError(message) {
        const error = this.$('#reset-password-error');
        if (!error) return false;
        error.textContent = message || '';
        error.hidden = !message;
        return Boolean(this.$('#reset-password-dialog')?.open);
    }

    #clearResetPasswordValidation() {
        const confirmInput = this.$('#confirm-password');
        if (confirmInput) {
            confirmInput.setCustomValidity('');
        }
    }

    #handleResetPasswordSubmit(e) {
        e.preventDefault();
        this.showResetPasswordError(null);
        const form = e.target;
        const newPassword = form.newPassword.value;
        const confirmPassword = form.confirmPassword.value;
        const confirmInput = this.$('#confirm-password');

        if (!form.checkValidity()) {
            form.reportValidity();
            return;
        }

        if (newPassword !== confirmPassword) {
            if (confirmInput) {
                confirmInput.setCustomValidity('Passwords do not match.');
                confirmInput.reportValidity();
            }
            return;
        }

        this.emit('user-reset-password', {id: this.#resetPasswordUserId, newPassword});
    }

    /**
     * Shows the loading overlay and disables the submit buttons, so a request cannot be sent twice
     * (the reset password dialog sits above the overlay, so its button needs disabling too).
     *
     * @param {boolean} value
     */
    set loading(value) {
        const overlay = this.$('#loading-overlay');
        if (overlay) {
            overlay.hidden = !value;
        }
        for (const button of this.$$('button[type="submit"]')) {
            button.disabled = value;
        }
    }

    /** @param {string} value client slug the listed users are filtered on */
    set filterSlug(value) {
        const input = this.$('#filter-slug');
        if (input) {
            input.value = value;
        }
    }

    /** @param {import('../../shell/types/client.types.js').UserViewModel[]} users */
    set users(users) {
        const container = this.$('#user-list');
        if (!container) return;

        container.innerHTML = '';

        if (users.length === 0) {
            const empty = document.createElement('empty-state');
            const title = document.createElement('span');
            title.slot = 'title';
            title.textContent = 'No users match this filter';
            empty.appendChild(title);
            empty.appendChild(document.createTextNode('Clear the filter to see everyone, or invite a new user by giving them the registration code on their client page.'));
            container.appendChild(empty);
            return;
        }

        for (const user of users) {
            const row = document.createElement('div');
            row.className = 'user-row';

            row.innerHTML = `
                <div class="user-main">
                    <div class="user-icon-wrapper">
                        <i aria-hidden="true" class="fa-regular fa-user"></i>
                    </div>
                    <div class="user-details">
                        <h3 class="user-email">${escapeHtml(user.email)}</h3>
                        <div class="user-meta">
                            <span class="badge badge-blue">${escapeHtml(user.clientName)}</span>
                            <span class="badge badge-gray">${escapeHtml(user.clientSlug)}</span>
                        </div>
                    </div>
                </div>
                <div class="user-actions">
                    <button type="button" class="btn btn-outline btn-sm btn-reset-pw" data-id="${user.id}" data-email="${escapeHtml(user.email)}"
                        aria-label="Reset password for ${escapeHtml(user.email)}">
                        <i aria-hidden="true" class="fa-solid fa-key"></i> Reset Password
                    </button>
                    <button type="button" class="btn btn-delete btn-sm btn-remove" data-id="${user.id}" data-email="${escapeHtml(user.email)}"
                        aria-label="Delete user ${escapeHtml(user.email)}">
                        <i aria-hidden="true" class="fa-solid fa-trash-can"></i> Delete
                    </button>
                </div>
            `;
            container.appendChild(row);
        }
    }

    /** @param {string[]} slugs */
    set clientOptions(slugs) {
        const datalist = this.$('#client-slugs');
        if (!datalist) return;
        datalist.innerHTML = '';
        for (const slug of slugs) {
            const option = document.createElement('option');
            option.value = slug;
            datalist.appendChild(option);
        }
    }
}

customElements.define('user-list', UserListComponent);
